import EastIcon from "@mui/icons-material/East";
import React, { useState, useEffect, useMemo } from "react";
import OrderImg from "../../../../assets/images/order-banner-image.png";
import PropTypes from "prop-types";
import config from "config/config";
import formatNumberWithCommas from "../../../../core/utils/formatNumberWithCommas";

// --- Helper Components ---

const TimeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M11.9994 9.5V13.5L14.4994 15M11.9994 5C7.30497 5 3.49939 8.80558 3.49939 13.5C3.49939 18.1944 7.30497 22 11.9994 22C16.6938 22 20.4994 18.1944 20.4994 13.5C20.4994 8.80558 16.6938 5 11.9994 5ZM11.9994 5V2M9.99939 2H13.9994M20.3284 5.59204L18.8284 4.09204L19.5784 4.84204M3.67041 5.59204L5.17041 4.09204L4.42041 4.84204"
      stroke="#FFCC00"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const centsToDollars = (cents) =>
  formatNumberWithCommas(Number(cents || 0) / 100, { asCurrency: true });

/**
 * Renders one side of the flip card (Label + Timer Digits)
 */
const CountdownFace = ({ label, timeLeft, isBackFace = false }) => (
  <div
    style={{
      position: "absolute",
      width: "100%",
      height: "100%",
      top: 0,
      left: 0,
      backfaceVisibility: "hidden",
      WebkitBackfaceVisibility: "hidden", // Safari support
      transform: isBackFace ? "rotateY(180deg)" : "rotateY(0deg)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <div className="text-center w-full">
      <p className="text-base uppercase md:-mt-2 md:mb-2 text-white md:block hidden font-bold tracking-wider">
        {label}
      </p>
      <div className="flex items-center mt-1 md:mt-0 md:items-start md:space-x-2 justify-center">
        <div className="md:hidden mr-2">
          <TimeIcon />
        </div>
        {["days", "hours", "minutes"].map((unit) => (
          <div
            key={unit}
            className="md:bg-white md:text-black text-center rounded p-2 md:w-14 flex flex-col items-center mx-1 md:mx-0 shadow-sm"
          >
            <span className="md:text-2xl text-base font-bold md:font-normal leading-none">
              {timeLeft?.[unit]}
            </span>
            <span className="text-[9px] md:text-xs uppercase mt-1">{unit}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

CountdownFace.propTypes = {
  label: PropTypes.string,
  timeLeft: PropTypes.object,
  isBackFace: PropTypes.bool,
};

// --- Main Component ---

const OrderNowBanner = ({ campaign, restaurantDetails, onExpireRefresh }) => {
  // 1. Extract Details
  const validDetails = campaign.validDetails || [];
  const fixedDetail = validDetails.find((d) => d.rewardType === "FIXED");
  const multiplierDetail = validDetails.find((d) => d.rewardType === "MULTIPLIER");

  const fixedExpiryDate = fixedDetail?.expirationDate;
  const multiplierExpiryDate = multiplierDetail?.expirationDate;

  // 2. Time Calculation Logic
  const calculateTimeLeft = (date) => {
    if (!date)
      return { days: null, hours: null, minutes: null, hasExpired: false, isLifetime: true };
    const expiryDateTime = new Date(date);
    const diff = expiryDateTime - new Date();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, hasExpired: true, isLifetime: false };
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      hasExpired: false,
      isLifetime: false,
    };
  };

  // State: Track time left (updated every second)
  const [fixedTimeLeft, setFixedTimeLeft] = useState(() => calculateTimeLeft(fixedExpiryDate));
  const [multiplierTimeLeft, setMultiplierTimeLeft] = useState(() =>
    calculateTimeLeft(multiplierExpiryDate)
  );

  // This determines which offer is theoretically "Active" for badges/text logic.
  const primaryOffer = useMemo(() => {
    if (fixedDetail && multiplierDetail) {
      // If both are lifetime, default to MULTIPLIER; otherwise pick the one with a real expiry
      if (!fixedExpiryDate && !multiplierExpiryDate) return "MULTIPLIER";
      if (!multiplierExpiryDate) return "FIXED";
      if (!fixedExpiryDate) return "MULTIPLIER";
      const fDate = new Date(fixedExpiryDate).getTime();
      const mDate = new Date(multiplierExpiryDate).getTime();
      return fDate === mDate ? "MULTIPLIER" : "FIXED";
    }
    return fixedDetail ? "FIXED" : "MULTIPLIER";
  }, [fixedDetail, multiplierDetail, fixedExpiryDate, multiplierExpiryDate]);

  // This determines which time is shown on the FRONT of the card.
  const [timerDisplay, setTimerDisplay] = useState(primaryOffer);

  // State: Hover for flip effect
  const [isHovered, setIsHovered] = useState(false);

  // Effect: Update numeric timers every second
  useEffect(() => {
    const timer = setInterval(() => {
      setFixedTimeLeft(calculateTimeLeft(fixedExpiryDate));
      setMultiplierTimeLeft(calculateTimeLeft(multiplierExpiryDate));
    }, 1000);
    return () => clearInterval(timer);
  }, [fixedExpiryDate, multiplierExpiryDate]);

  useEffect(() => {
    if (!onExpireRefresh) return;

    const fixedExpired = !fixedTimeLeft.isLifetime && fixedTimeLeft.hasExpired;
    const multiplierExpired = !multiplierTimeLeft.isLifetime && multiplierTimeLeft.hasExpired;
    if (fixedExpired || multiplierExpired) {
      onExpireRefresh();
    }
  }, [fixedTimeLeft.hasExpired, multiplierTimeLeft.hasExpired]);

  // Effect: Toggle the TIMER DISPLAY every 30 seconds
  useEffect(() => {
    if (fixedDetail && multiplierDetail) {
      // If either date is null (lifetime), no toggling needed
      if (!fixedExpiryDate || !multiplierExpiryDate) {
        setTimerDisplay(primaryOffer);
        return;
      }

      const fDate = new Date(fixedExpiryDate).getTime();
      const mDate = new Date(multiplierExpiryDate).getTime();

      // Only toggle if dates are DIFFERENT
      if (fDate !== mDate) {
        setTimerDisplay(primaryOffer);

        const toggleInterval = setInterval(() => {
          setTimerDisplay((prev) => (prev === "FIXED" ? "MULTIPLIER" : "FIXED"));
        }, 30000);

        return () => clearInterval(toggleInterval);
      } else {
        // If dates are same, force Multiplier and DO NOT toggle
        setTimerDisplay("MULTIPLIER");
      }
    } else {
      setTimerDisplay(primaryOffer);
    }
  }, [fixedDetail, multiplierDetail, fixedExpiryDate, multiplierExpiryDate, primaryOffer]);

  // Hide banner if all offers have expired (lifetime offers never expire)
  const fixedGone = !fixedDetail || (!fixedTimeLeft.isLifetime && fixedTimeLeft.hasExpired);
  const multiplierGone =
    !multiplierDetail || (!multiplierTimeLeft.isLifetime && multiplierTimeLeft.hasExpired);
  if (fixedGone && multiplierGone) {
    return null;
  }

  // --- UI Helpers ---
  const landingBaseUrl =
    config[process.env.REACT_APP_ENV?.toUpperCase() || "LOCAL"].API_LANDING_PAGE_BASE_URL;
  const landingUrl =
    restaurantDetails?.scope === "BRAND"
      ? `${landingBaseUrl}/brand/${restaurantDetails?.brandId}?code=${campaign.code}`
      : `${landingBaseUrl}/restaurants/${restaurantDetails?.restaurantPrefix}?code=${campaign.code}`;
  const hasOrderNowUrl =
    restaurantDetails?.orderNowUrl && restaurantDetails.orderNowUrl.trim() !== "";
  const minValue = campaign.minimumOrderValueInCents;
  const isOverride = !!campaign.override;
  const isLapsed = campaign.marketingFor === "LAPSED";
  const eligibleCount = Number(campaign.eligibleOrdersCount);
  const hasCap = Number.isFinite(eligibleCount) && eligibleCount > 0;
  const maxCashbackCents = Number(campaign.maxCashbackInCents);
  const hasMaxCashback = Number.isFinite(maxCashbackCents) && maxCashbackCents > 0;

  const allLifetime = fixedTimeLeft.isLifetime && multiplierTimeLeft.isLifetime;
  const anyLifetime = fixedTimeLeft.isLifetime || multiplierTimeLeft.isLifetime;
  const areDatesSame =
    fixedExpiryDate &&
    multiplierExpiryDate &&
    new Date(fixedExpiryDate).getTime() === new Date(multiplierExpiryDate).getTime();

  const showCountdown = !allLifetime;

  let frontFace = { label: "", time: {} };
  let backFace = { label: "", time: {} };

  if (timerDisplay === "FIXED") {
    let label = "First Order Offer Expires In";

    if (!multiplierDetail) {
      label = "Offer Expires In";
    }

    frontFace = { label: label, time: fixedTimeLeft };
    backFace = { label: "Offer Expires In", time: multiplierTimeLeft };
  } else {
    let label = "Offer Expires In";

    frontFace = { label: label, time: multiplierTimeLeft };
    backFace = { label: "First Order Offer Expires In", time: fixedTimeLeft };
  }
  const canFlip =
    fixedDetail &&
    multiplierDetail &&
    !fixedTimeLeft.hasExpired &&
    !multiplierTimeLeft.hasExpired &&
    !anyLifetime &&
    !areDatesSame;

  const renderCampaignDetails = (code, details) => {
    if (!details || details.length === 0) return null;

    const allOrdersDetail = details.find((d) => d.rewardCondition === "ALL_ORDERS");
    const firstOrderDetail = details.find((d) => d.rewardCondition === "FIRST_ORDER");

    const buildHeadline = (detail) => {
      const { rewardType, fixedCashback, multiplierCashback, rewardCondition } = detail;
      const condText = rewardCondition === "FIRST_ORDER" ? "on your FIRST order" : "on EVERY order";

      if (isOverride) {
        if (rewardType === "FIXED") {
          return `Earn ${formatNumberWithCommas(fixedCashback)} cashback points ($${centsToDollars(
            fixedCashback
          )}) ${condText}`;
        } else {
          return `Earn ${multiplierCashback}X cashback points ${condText}`;
        }
      } else {
        if (rewardType === "FIXED") {
          return `Enjoy an additional reward of ${formatNumberWithCommas(
            fixedCashback
          )} cashback points ($${centsToDollars(
            fixedCashback
          )}) ${condText}, on top of your existing cashback incentive,`;
        } else {
          return `Enjoy an additional +${multiplierCashback}X cashback ${condText}, on top of your existing cashback incentive,`;
        }
      }
    };

    return (
      <div className="mb-4">
        {allOrdersDetail && (
          <h2 className="text-xl font-semibold text-left">
            {buildHeadline(allOrdersDetail, true)} from{" "}
            <span
              onClick={() => window.open(landingUrl, "_blank")}
              className="hover:underline cursor-pointer"
            >
              {restaurantDetails?.restaurantName}
            </span>
          </h2>
        )}

        {allOrdersDetail && firstOrderDetail && (
          <div className="flex flex-col md:flex-row md:items-baseline gap-2 text-base md:text-lg mt-2 text-left font-medium">
            {(() => {
              const shouldShowBadge = primaryOffer === "FIXED" || areDatesSame;

              if (
                shouldShowBadge &&
                firstOrderDetail &&
                firstOrderDetail.expirationDate &&
                !fixedTimeLeft.hasExpired
              ) {
                return (
                  <span
                    className="inline-block text-[12px] leading-[100%] font-semibold px-2 py-2 rounded-full"
                    style={{ backgroundColor: "#FFF5EA", color: "#B45309" }}
                  >
                    LIMITED TIME OFFER
                  </span>
                );
              }
              return null;
            })()}
            <div className="flex flex-col md:flex-row md:items-center gap-0">
              <span className="flex flex-col">{buildHeadline(firstOrderDetail, false)}</span>
            </div>
          </div>
        )}

        {!allOrdersDetail && firstOrderDetail && (
          <h2 className="text-lg font-semibold text-left">
            {buildHeadline(firstOrderDetail, true)} from{" "}
            <span
              onClick={() => window.open(landingUrl, "_blank")}
              className="hover:underline cursor-pointer"
            >
              {restaurantDetails?.restaurantName}
            </span>
          </h2>
        )}

        {minValue !== "0" && (
          <p className="text-base md:text-base text-white mt-2 text-left">
            Minimum order value: ${centsToDollars(minValue)}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="w-full bg-[#238856] text-white rounded-[16px] overflow-hidden">
      <div className="container mx-auto p-[15px] lg:py-0 lg:px-6 lg:pt-2 flex flex-col md:flex-row justify-between">
        {/* Left Content */}
        <div
          style={{ marginTop: !hasOrderNowUrl && 22 }}
          className="flex-1 mt-1 text-center md:text-left mb-4 md:mb-0"
        >
          {renderCampaignDetails(campaign.code, campaign.validDetails)}

          {hasCap && (
            <p className="text-sm opacity-90 text-left m-0">
              Valid for up to {eligibleCount} catering order{eligibleCount > 1 ? "s" : ""}
              {campaign.scope === "BRAND" ? " across all stores under this brand" : ""}.
            </p>
          )}

          {hasMaxCashback && (
            <p className="text-sm opacity-90 text-left m-0">
              Earn up to ${centsToDollars(maxCashbackCents)} cashback per order
              {isOverride && isLapsed
                ? " (or your standard rewards, whichever is higher)"
                : ""}
              .
            </p>
          )}

          {hasOrderNowUrl && (
            <button
              onClick={() => window.open(restaurantDetails.orderNowUrl, "_blank")}
              className="items-center bg-white text-emerald-800 px-4 py-2 rounded-md font-medium text-sm hover:bg-emerald-50 transition-colors mt-1 mb-3 hidden md:inline-flex"
            >
              Order now <EastIcon className="ml-2 h-4 w-4 " />
            </button>
          )}
        </div>

        {/* Right Content (Image + Countdown) */}
        <div className="flex gap-10 flex-row items-center justify-between md:justify-start">
          <div className="hidden lg:block w-32 md:w-[208px] h-[150px] mb-4 md:mb-0">
            <img src={OrderImg} alt="Person holding food items" className="h-full w-full" />
          </div>

          {/* 3D FLIP CARD CONTAINER */}
          {showCountdown && (
            <div
              className="relative w-[220px] h-[90px] md:w-[280px] md:h-[80px]"
              onMouseEnter={() => canFlip && setIsHovered(true)}
              onMouseLeave={() => canFlip && setIsHovered(false)}
              style={{ perspective: "1000px" }}
            >
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  transition: "transform 0.6s",
                  transformStyle: "preserve-3d",
                  transform: canFlip && isHovered ? "rotateY(180deg)" : "rotateY(0deg)",
                }}
              >
                <CountdownFace label={frontFace.label} timeLeft={frontFace.time} />

                {canFlip && (
                  <CountdownFace
                    label={backFace.label}
                    timeLeft={backFace.time}
                    isBackFace={true}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

OrderNowBanner.propTypes = {
  campaign: PropTypes.shape({
    code: PropTypes.string,
    scope: PropTypes.string,
    override: PropTypes.bool,
    marketingFor: PropTypes.string,
    minimumOrderValueInCents: PropTypes.string,
    maxCashbackInCents: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    eligibleOrdersCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    validDetails: PropTypes.arrayOf(
      PropTypes.shape({
        rewardType: PropTypes.string,
        rewardCondition: PropTypes.string,
        fixedCashback: PropTypes.number,
        multiplierCashback: PropTypes.number,
        expirationDate: PropTypes.string,
        validityPeriodInDays: PropTypes.number,
      })
    ),
  }),
  restaurantDetails: PropTypes.any,
  onExpireRefresh: PropTypes.func,
};

export default OrderNowBanner;
